import type { CSSProperties, ReactNode } from 'react';

import { cn } from '@ps/ui/utils';

import {
  CONTENT_TABS_ANCHOR_PATH,
  linkNodeAttr,
} from '../../linking/functions/linkNodePath';

interface Props {
  /** Opaque background for the sticky strip (matches the bio card column). */
  stripBgColor: string;
  /**
   * CSS `top` for the sticky strip. The editor passes its measured toolbar
   * height var (`var(--bio-toolbar-h, 0px)`); the visitor leaves the default so
   * the strip pins to the top of the scroll container. Host-specific, so it's a
   * prop — the view bakes in no host awareness.
   */
  stickyTop?: string;
  /** The tab items — `<a>` anchors for the visitor, `<button>`s for the editor. */
  children: ReactNode;
  /**
   * Open-ended trailing slot at the row's end (e.g. the editor's "manage tabs"
   * pencil). The view stays unaware of who fills it.
   */
  trailing?: ReactNode;
  /** Container customization, merged onto the root. */
  className?: string;
}

/**
 * Pure, intent-agnostic layout shell for the bio content-tab strip: a sticky,
 * opaque-backed row whose tabs each take an equal share (`flex-1`), plus an
 * optional trailing slot. It renders ONLY the strip — the active tab body is
 * the orchestrator's concern (layout-agnostic), and whether a tab is a link or
 * a button is decided by the caller's children.
 */
export function ContentTabStripView({
  stripBgColor,
  stickyTop = '0px',
  children,
  trailing,
  className,
}: Props) {
  const stickyStyle: CSSProperties = {
    top: stickyTop,
    backgroundColor: stripBgColor,
  };

  return (
    // Sticky lives on the ROOT so its containing block is the shared parent that
    // also holds the active tab body (the strip + body render as siblings under
    // the bio card column) — that parent's height is what gives the strip room to
    // travel and pin as the body scrolls under it. Wrapping the sticky div in its
    // own strip-height parent instead confines it to that height, so it scrolls
    // away immediately rather than sticking.
    //
    // The root also marks the top of the content section — a cold/hard load of a
    // tab route implicitly jumps here (see useBioHashScroll); `scroll-mt` gives
    // the jump a little breathing room.
    //
    // `z-[25]`: the body scrolls UNDER the pinned strip, so the strip must
    // out-stack everything the bodies layer (rail arrows z-10, card menus z-[9],
    // edit chips and badges up to z-20) — yet stay below the editor toolbar
    // (z-30) it pins beneath.
    <div
      className={cn('sticky z-[25] w-full scroll-mt-4', className)}
      style={stickyStyle}
      {...linkNodeAttr(CONTENT_TABS_ANCHOR_PATH)}
    >
      <div className="relative flex items-stretch px-3">
        <div role="tablist" className="flex flex-1 items-stretch">
          {children}
        </div>
        {trailing}
      </div>
    </div>
  );
}
